/**
 * Account Settings Page
 * Manage user profile and preferences
 */

import React, { useState } from 'react';
import { Container, Main, Flex } from '../../../components/styled/Layout';
import { Card, CardContent } from '../../../components/styled/Card';
import { PrimaryButton, SecondaryButton } from '../../../components/styled/Button';
import { FormField } from '../../../components/styled/Form';
import { useAuth } from '../../../contexts/AuthContext';
import apiService from '../../../services/apiService';
import AddressManager from '../components/AddressManager';
import useShopFeatures from '../hooks/useShopFeatures';
import './Shop.css';

const Account = () => {
  const { user, updateUserProfile } = useAuth();
  const features = useShopFeatures();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Profile form
  const [profileData, setProfileData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || ''
  });

  // Password form
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Notification preferences
  const [notifications, setNotifications] = useState({
    orderUpdates: true,
    promotions: false,
    newsletter: false
  });

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await apiService.put('/auth/profile', profileData);
      
      // Update auth context if needed
      if (updateUserProfile) {
        updateUserProfile(profileData);
      }
      
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to update profile' });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    // Validation
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      setLoading(false);
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters' });
      setLoading(false);
      return;
    }

    try {
      await apiService.post('/auth/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      
      setMessage({ type: 'success', text: 'Password changed successfully!' });
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      console.error('Error changing password:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to change password' });
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await apiService.put('/auth/notifications', notifications);
      setMessage({ type: 'success', text: 'Notification preferences updated!' });
    } catch (error) {
      console.error('Error updating notifications:', error);
      setMessage({ type: 'error', text: 'Failed to update preferences' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Main>
        <div className="shop-page account-page">
          <h1>My Account</h1>

          {/* Tabs */}
          <div className="account-tabs">
            <button 
              className={`tab ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              Profile
            </button>
            <button 
              className={`tab ${activeTab === 'password' ? 'active' : ''}`}
              onClick={() => setActiveTab('password')}
            >
              Password
            </button>
            <button 
              className={`tab ${activeTab === 'notifications' ? 'active' : ''}`}
              onClick={() => setActiveTab('notifications')}
            >
              Notifications
            </button>
            {features.addressBook && (
              <button 
                className={`tab ${activeTab === 'addresses' ? 'active' : ''}`}
                onClick={() => setActiveTab('addresses')}
              >
                Address Book
              </button>
            )}
          </div>

          {/* Message Display */}
          {message.text && (
            <div className={`message ${message.type}`} style={{ 
              padding: '12px 16px', 
              marginBottom: '20px', 
              borderRadius: '4px',
              backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da',
              color: message.type === 'success' ? '#155724' : '#721c24',
              border: `1px solid ${message.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`
            }}>
              {message.text}
            </div>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <Card>
              <CardContent>
                <h2>Profile Information</h2>
                <form onSubmit={handleProfileUpdate}>
                  <FormField>
                    <label>First Name</label>
                    <input
                      type="text"
                      value={profileData.firstName}
                      onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
                      required
                    />
                  </FormField>

                  <FormField>
                    <label>Last Name</label>
                    <input
                      type="text"
                      value={profileData.lastName}
                      onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
                      required
                    />
                  </FormField>

                  <FormField>
                    <label>Email</label>
                    <input
                      type="email"
                      value={profileData.email}
                      onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                      required
                      disabled
                    />
                    <small>Email cannot be changed</small>
                  </FormField>

                  <FormField>
                    <label>Phone (Optional)</label>
                    <input
                      type="tel"
                      value={profileData.phone}
                      onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                      placeholder="+1 (555) 123-4567"
                    />
                  </FormField>

                  <Flex gap="12px" justify="flex-end">
                    <SecondaryButton type="button" onClick={() => setProfileData({
                      firstName: user?.firstName || '',
                      lastName: user?.lastName || '',
                      email: user?.email || '',
                      phone: user?.phone || ''
                    })}>
                      Reset
                    </SecondaryButton>
                    <PrimaryButton type="submit" disabled={loading}>
                      {loading ? 'Saving...' : 'Save Changes'}
                    </PrimaryButton>
                  </Flex>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Password Tab */}
          {activeTab === 'password' && (
            <Card>
              <CardContent>
                <h2>Change Password</h2>
                <form onSubmit={handlePasswordChange}>
                  <FormField>
                    <label>Current Password</label>
                    <input
                      type="password"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                      required
                    />
                  </FormField>

                  <FormField>
                    <label>New Password</label>
                    <input
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                      required
                      minLength={8}
                    />
                    <small>Minimum 8 characters</small>
                  </FormField>

                  <FormField>
                    <label>Confirm New Password</label>
                    <input
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                      required
                    />
                  </FormField>

                  <Flex gap="12px" justify="flex-end">
                    <SecondaryButton type="button" onClick={() => setPasswordData({
                      currentPassword: '',
                      newPassword: '',
                      confirmPassword: ''
                    })}>
                      Cancel
                    </SecondaryButton>
                    <PrimaryButton type="submit" disabled={loading}>
                      {loading ? 'Changing...' : 'Change Password'}
                    </PrimaryButton>
                  </Flex>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <Card>
              <CardContent>
                <h2>Notification Preferences</h2>
                <form onSubmit={handleNotificationUpdate}>
                  <div className="notification-options">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={notifications.orderUpdates}
                        onChange={(e) => setNotifications({ ...notifications, orderUpdates: e.target.checked })}
                      />
                      <div>
                        <strong>Order Updates</strong>
                        <small>Get notified about your order status</small>
                      </div>
                    </label>

                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={notifications.promotions}
                        onChange={(e) => setNotifications({ ...notifications, promotions: e.target.checked })}
                      />
                      <div>
                        <strong>Promotions & Deals</strong>
                        <small>Receive exclusive offers and discounts</small>
                      </div>
                    </label>

                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={notifications.newsletter}
                        onChange={(e) => setNotifications({ ...notifications, newsletter: e.target.checked })}
                      />
                      <div>
                        <strong>Newsletter</strong>
                        <small>Monthly updates and product news</small>
                      </div>
                    </label>
                  </div>

                  <Flex gap="12px" justify="flex-end" style={{ marginTop: '24px' }}>
                    <PrimaryButton type="submit" disabled={loading}>
                      {loading ? 'Saving...' : 'Save Preferences'}
                    </PrimaryButton>
                  </Flex>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Address Book Tab */}
          {activeTab === 'addresses' && features.addressBook && (
            <AddressManager />
          )}
        </div>
      </Main>
    </Container>
  );
};

export default Account;

